import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import "bulma/css/bulma.min.css";
import "./styles.css";
import {
  buildEntitiesUrl,
  fetchEntities,
  fetchEntityDetail,
  fetchLayers,
} from "./api";
import Sidebar from "./components/Sidebar";
import HistoryRangeBar from "./components/HistoryRangeBar";
import AboutPanel from "./components/AboutPanel";
import MapLegend from "./components/MapLegend";
import useEntityList from "./useEntityList";
import ListCard from "./components/ListCard";
import useHistoryRange from "./useHistoryRange";
import useHistorySelection from "./useHistorySelection";
import { getHistoryFilterOverrides } from "./historyFilterUtils";
import {
  getNextLayerFilterState,
  getNextTagFilterState,
  getTagsForLayers,
} from "./selectionUtils";
import listModeBg from "../list_mode_bg.png";

const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] };
const MAP_STYLE =
  process.env.REACT_APP_MAP_STYLE ||
  "mapbox://styles/contredanse/clyidx55r00zn01qvei5lhqm3";
const STYLE_NOISE_LAYERS = [
  "Structures-dartistes",
  "Formations",
  "formations",
  "Ressources-et-accompagnements",
  "Aides-et-subventions",
  "Residences-et-location-de-salles",
  "Diffusion-et-programmation",
  "Durabilite",
  // Additional baked layers sometimes present in the style
  "Structures-dartistes-copy",
  "Structures-dartistes-symbol",
  "structures-dartistes",
  "structures-dartistes-symbol",
  "structures-dartistes-copy",
  "Structures-dartistes-2",
  "structures-dartistes-2",
  "structures-dartistes-label",
  "structures-dartistes-cluster",
  "structures-dartistes-cluster-count",
];

const CURSOR_ICON_PATHS = {
  default: "/icons/cursor_default.svg",
  approximative: "/icons/cursor_approximative.svg",
  virtual: "/icons/cursor_virtual.svg",
};
const ICON_TARGET_HEIGHT = 48; // px for crisp rendering before pixel ratio
const ICON_PIXEL_RATIO = 2;
const HISTORY_DEFAULT_FROM = 1970;
const HISTORY_DEFAULT_TO = Math.max(HISTORY_DEFAULT_FROM, new Date().getFullYear() - 1);
const HISTORY_DEFAULT_LAYER_IDS = [1, 7];

const pickLabel = (props = {}, language = "en") => {
  const order = [
    `label_${language}`,
    "label_en",
    "label_fr",
    "label_nl",
    "label",
  ];
  const val = order
    .map((key) => props[key])
    .find((v) => typeof v === "string" && v.trim().length > 0);
  return (val || "").trim();
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

const firstAddress = (addresses = []) => toArray(addresses)[0] || {};

const findContacts = (contacts = [], needle) => {
  const list = toArray(contacts);
  const lowerNeedle = needle.toLowerCase();
  return list
    .filter(
      (contact) =>
        (contact.type && contact.type.toLowerCase().includes(lowerNeedle)) ||
        (contact.label_fr &&
          contact.label_fr.toLowerCase().includes(lowerNeedle)) ||
        (contact.label_en &&
          contact.label_en.toLowerCase().includes(lowerNeedle)) ||
        (contact.label_nl &&
          contact.label_nl.toLowerCase().includes(lowerNeedle))
    )
    .map((c) => c.value);
};

const formatTagsString = (taxonomies = [], language = "en") =>
  taxonomies
    .map((t) => {
      const label =
        t[`label_${language}`] ||
        t.label_en ||
        t.label_fr ||
        t.label_nl ||
        t.code ||
        "";
      return label;
    })
    .join(" / ");

const toTaxonomyArray = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
};

  const translations = {
    en: {
    categories: "Categories",
    keywords: "Keywords",
    filterByKeywords: "Filter by keywords",
    noResults: "No matching results",
    searchPlaceholder: "Search...",
    tagline: "Mapping the dance sector in Belgium",
    initiative:
      "An initiative by Contredanse in collaboration with Danscentrumjette and developed in connection with the Welcome Pack of the RAC.",
    initiativePrefix: "An initiative by",
    initiativeCollab: "in collaboration with",
    initiativeConn: "and developed in connection with the",
    initiativeOf: "of the",
    loading: "Loading",
    creationYear: "Start year of activity",
    endYear: "End year of activity",
    lang: "Lang",
    organization: "Organization",
    view: "View",
    map: "Map",
    list: "List",
    results: "Results",
    loadMore: "Load more",
    searchAction: "Search",
    filters: "Filters",
    category: "Category",
    information: "Information",
    history: "History",
    historyRange: "History range",
    yearFrom: "From",
    yearTo: "To",
    historyUnavailable: "Range unavailable",
    email: "Email",
    phone: "Phone",
    website: "Website",
    showMore: "Show more",
    showLess: "Show less",
    viewWebsite: "View website",
    endOfList: "End of list",
    publicRelations: "Public relations",
    aliases: "Aliases",
    learnMore: "LEARN MORE",
    contribute: "CONTRIBUTE TO THE MAP",
    legendExact: "EXACT ADDRESS",
    legendArea: "GEOGRAPHIC AREA",
    legendVirtual: "VIRTUAL PROJECT",
  },
    fr: {
    categories: "Catégories",
    keywords: "Mots-clés",
    filterByKeywords: "Filtrer par mots-clés",
    noResults: "Aucun résultat correspondant",
    searchPlaceholder: "Recherche...",
    tagline: "Cartographie du secteur de la danse en Belgique",
    initiative:
      "Une initiative de Contredanse en collaboration avec le Danscentrumjette et développée en connexion avec le Welcome Pack de la RAC.",
    initiativePrefix: "Une initiative de",
    initiativeCollab: "en collaboration avec le",
    initiativeConn: "et développée en connexion avec le",
    initiativeOf: "de la",
    loading: "Chargement",
    creationYear: "Année début d'activité",
    endYear: "Année de fin d'activité",
    lang: "Langue",
    organization: "Organisation",
    view: "Vue",
    map: "Carte",
    list: "Liste",
    results: "Résultats",
    loadMore: "Charger plus",
    searchAction: "Rechercher",
    filters: "Filtres",
    category: "Catégorie",
    information: "Information",
    history: "Histoire",
    historyRange: "Plage historique",
    yearFrom: "De",
    yearTo: "A",
    historyUnavailable: "Plage indisponible",
    email: "Email",
    phone: "Téléphone",
    website: "Site web",
    showMore: "Afficher plus",
    showLess: "Afficher moins",
    viewWebsite: "Voir le site web",
    endOfList: "Fin de la liste",
    publicRelations: "Relations publiques",
    aliases: "Alias",
    learnMore: "EN SAVOIR +",
    contribute: "CONTRIBUER A LA CARTE",
    legendExact: "ADRESSE PRÉCISE",
    legendArea: "ZONE GÉOGRAPHIQUE",
    legendVirtual: "PROJET VIRTUEL",
  },
    nl: {
    categories: "Categorieën",
    keywords: "Trefwoorden",
    filterByKeywords: "Filter op trefwoorden",
    noResults: "Geen overeenkomende resultaten",
    searchPlaceholder: "Zoeken...",
    tagline: "Kaart van de danssector in België",
    initiative:
      "Een initiatief van Contredanse in samenwerking met Danscentrumjette en ontwikkeld in verbinding met de Welcome Pack van de RAC.",
    initiativePrefix: "Een initiatief van",
    initiativeCollab: "in samenwerking met",
    initiativeConn: "en ontwikkeld in verbinding met de",
    initiativeOf: "van de",
    loading: "Laden",
    creationYear: "Jaar van start van activiteit",
    endYear: "Eindejaar van activiteit",
    lang: "Taal",
    organization: "Organisatie",
    view: "Weergave",
    map: "Kaart",
    list: "Lijst",
    results: "Resultaten",
    loadMore: "Meer laden",
    searchAction: "Zoeken",
    filters: "Filters",
    category: "Categorie",
    information: "Informatie",
    history: "Geschiedenis",
    historyRange: "Historisch bereik",
    yearFrom: "Van",
    yearTo: "Tot",
    historyUnavailable: "Bereik niet beschikbaar",
    email: "E-mail",
    phone: "Telefoon",
    website: "Website",
    showMore: "Meer tonen",
    showLess: "Minder tonen",
    viewWebsite: "Website bekijken",
    endOfList: "Einde van de lijst",
    publicRelations: "Publieke relaties",
    aliases: "Aliassen",
    learnMore: "MEER INFO",
    contribute: "BIJDRAGEN AAN DE KAART",
    legendExact: "EXACT ADRES",
    legendArea: "GEOGRAFISCHE ZONE",
    legendVirtual: "VIRTUEEL PROJECT",
  },
};

const t = (language, key) =>
  translations[language]?.[key] || translations.en[key] || key;

const SearchBar = ({
  searchQuery,
  setSearchQuery,
  language,
  t,
  width = 420,
  minLength = 0,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      width,
      height: 40,
      borderRadius: 10,
      border: "1px solid #e5e5e5",
      padding: "0 8px",
      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
      background: "rgba(255,255,255,0.92)",
      gap: 8,
      margin: "0 auto",
      pointerEvents: "auto",
    }}
  >
    <img
      src="/icons/ico_search.svg"
      alt=""
      aria-hidden="true"
      style={{ width: 16, height: 16, opacity: 0.7 }}
    />
    <input
      type="text"
      placeholder={t(language, "searchPlaceholder")}
      value={searchQuery || ""}
      onChange={(e) => setSearchQuery(e.target.value)}
      style={{
        flex: 1,
        border: "none",
        outline: "none",
        background: "transparent",
        fontSize: 14,
      }}
    />
    <button
      disabled={!searchQuery || (searchQuery || "").trim().length < minLength}
      onClick={() => setSearchQuery((searchQuery || "").trim())}
      style={{
        border: "none",
        background:
          searchQuery && (searchQuery || "").trim().length >= minLength
            ? "#265b4f"
            : "transparent",
        color:
          searchQuery && (searchQuery || "").trim().length >= minLength
            ? "white"
            : "#9ca3af",
        borderRadius: 8,
        padding: "6px 10px",
        cursor:
          searchQuery && (searchQuery || "").trim().length >= minLength
            ? "pointer"
            : "not-allowed",
        fontWeight: 700,
        fontSize: 12,
      }}
    >
      {t(language, "searchAction")}
    </button>
  </div>
);

const MapComponent = () => {
  const [map, setMap] = useState(null);
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [viewMode, setViewMode] = useState("map");
  const [historyActive, setHistoryActive] = useState(false);
  const { range: historyRange, loading: isHistoryLoading } = useHistoryRange({
    active: historyActive,
  });
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);
  const detectLang = () => {
    const nav = navigator.language || navigator.userLanguage || "en";
    if (nav.startsWith("fr")) return "fr";
    if (nav.startsWith("nl")) return "nl";
    return "en";
  };
  const [language, setLanguage] = useState(detectLang());
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const languageRef = useRef("en");

  const [layers, setLayers] = useState([]);
  const [taxonomies, setTaxonomies] = useState([]);
  const [selectedLayers, setSelectedLayers] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [layerColorMap, setLayerColorMap] = useState({});
  const [taxonomyColorMap, setTaxonomyColorMap] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const SEARCH_MIN_LENGTH = 2;
  const SEARCH_DEBOUNCE_MS = 300;
  const HISTORY_DEBOUNCE_MS = 300;
  const [currentFeature, setCurrentFeature] = useState(null);
  const [viewportBbox, setViewportBbox] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [, setMapFetchInFlight] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [, setShouldRetryFirstFetch] = useState(false);
  const [shouldAutoFitNextFetch, setShouldAutoFitNextFetch] = useState(false);
  const [lastFetchEmpty, setLastFetchEmpty] = useState(false);
  const isMobile = viewportWidth <= 768;
  const isNarrow = viewportWidth <= 900;
  const isListMode = viewMode === "list";
  const [fullInfoBox, setFullInfoBox] = useState(true);
  const [totalAvailable, setTotalAvailable] = useState(null);
  const isMobileRef = useRef(isMobile);

  const layersAbortRef = useRef(null);
  const entitiesAbortRef = useRef(null);
  const moveEndTimeout = useRef(null);
  const layerColorMapRef = useRef({});
  const taxonomyColorMapRef = useRef({});
  const lastFetchKeyRef = useRef(null);
  const entitiesCacheRef = useRef(new Map());
  const coverageCacheRef = useRef(new Map()); // key -> { bbox, grouped }
  const markerImagesRef = useRef(new Set());
  const markerIconCacheRef = useRef(new Map());
  const entityDetailsCacheRef = useRef(new Map());
  const detailAbortRef = useRef(null);
  const detailActiveIdRef = useRef(null);
  const historyPrevFiltersRef = useRef(null);
  const featurePropsByIdRef = useRef(new Map());
  const currentFeatureRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const firstFetchTimerRef = useRef(null);
  const firstFetchTimeoutRef = useRef(null);
  const retryIfEmptyTimerRef = useRef(null);
  const listContainerRef = useRef(null);
  const listSentinelRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 900);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  const effectiveSearchQuery = debouncedSearchQuery;
  const {
    draft: historyDraft,
    applied: historyApplied,
    updateDraft: updateHistoryDraft,
    commitDraft: commitHistoryDraft,
  } = useHistorySelection({
    range: historyRange,
    debounceMs: HISTORY_DEBOUNCE_MS,
    defaultFrom: HISTORY_DEFAULT_FROM,
    defaultTo: HISTORY_DEFAULT_TO,
  });

  useEffect(() => {
    layerColorMapRef.current = layerColorMap;
  }, [layerColorMap]);

  useEffect(() => {
    taxonomyColorMapRef.current = taxonomyColorMap;
  }, [taxonomyColorMap]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = (searchQuery || "").trim();
      setDebouncedSearchQuery(
        trimmed.length >= SEARCH_MIN_LENGTH ? trimmed : ""
      );
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (isNarrow) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isNarrow]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    languageRef.current = language;
    console.log("[lang] language changed to", language);
    const id = currentFeature?.id;
    if (id && featurePropsByIdRef.current.has(id)) {
      const raw = featurePropsByIdRef.current.get(id);
      setCurrentFeature(
        normalizeFeatureProps(
          raw,
          languageRef.current || language,
          featurePropsByIdRef.current
        )
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  useEffect(() => {
    currentFeatureRef.current = currentFeature;
  }, [currentFeature]);

  const handleFeatureSelection = useCallback(
    (feature, { collapseInfoBoxOnMobile = false } = {}) => {
      if (!feature) {
        setCurrentFeature(null);
        return;
      }
      const same =
        currentFeatureRef.current?.id &&
        feature.id &&
        currentFeatureRef.current.id === feature.id;
      setCurrentFeature(same ? null : feature);
      if (!same) {
        setFullInfoBox(true);
      }
    },
    []
  );

  useEffect(() => {
    console.log("[history] state", {
      historyActive,
      viewMode,
      historyRange,
      historyDraft,
      historyApplied,
      isHistoryLoading,
    });
  }, [
    historyActive,
    viewMode,
    historyRange,
    historyDraft,
    historyApplied,
    isHistoryLoading,
  ]);

  const activeLayerIds = useMemo(
    () =>
      selectedLayers.length > 0
        ? selectedLayers
        : layers.map((layer) => layer.id),
    [selectedLayers, layers]
  );
  const activeLayerTagCodes = useMemo(
    () => getTagsForLayers(taxonomies, activeLayerIds),
    [taxonomies, activeLayerIds]
  );
  const normalizedSelectedTags = useMemo(() => {
    const activeTagSet = new Set(activeLayerTagCodes);
    return selectedTags.filter((tag) => activeTagSet.has(tag));
  }, [selectedTags, activeLayerTagCodes]);
  const useTaxonomyFilter = useMemo(
    () =>
      normalizedSelectedTags.length > 0 &&
      normalizedSelectedTags.length !== activeLayerTagCodes.length,
    [normalizedSelectedTags, activeLayerTagCodes]
  );
  const activeTaxonomyCodes = useMemo(
    () => (useTaxonomyFilter ? normalizedSelectedTags : []),
    [useTaxonomyFilter, normalizedSelectedTags]
  );
  const historyFilterOverrides = useMemo(
    () =>
      getHistoryFilterOverrides({
        historyActive,
        layerIds: activeLayerIds,
        taxonomyCodes: activeTaxonomyCodes,
        bbox: viewportBbox,
      }),
    [historyActive, activeLayerIds, activeTaxonomyCodes, viewportBbox]
  );
  const historyRangeAvailable =
    historyRange &&
    typeof historyRange.minYear === "number" &&
    typeof historyRange.maxYear === "number";
  const historyYearFrom = useMemo(() => {
    if (!historyActive || !historyRangeAvailable) return undefined;
    if (typeof historyApplied.from !== "number") return undefined;
    return historyApplied.from > historyRange.minYear
      ? historyApplied.from
      : undefined;
  }, [historyActive, historyRangeAvailable, historyRange, historyApplied]);
  const historyYearTo = useMemo(() => {
    if (!historyActive || !historyRangeAvailable) return undefined;
    if (typeof historyApplied.to !== "number") return undefined;
    return historyApplied.to < historyRange.maxYear
      ? historyApplied.to
      : undefined;
  }, [historyActive, historyRangeAvailable, historyRange, historyApplied]);

  useEffect(() => {
    if (!historyActive || !historyRangeAvailable) return;
    updateHistoryDraft({
      from: HISTORY_DEFAULT_FROM,
      to: HISTORY_DEFAULT_TO,
    });
    commitHistoryDraft();
  }, [
    historyActive,
    historyRangeAvailable,
    historyRange,
    updateHistoryDraft,
    commitHistoryDraft,
  ]);

  const loadImageElement = (src) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });

  const loadTintedIcon = async (path, color, targetHeight = ICON_TARGET_HEIGHT) => {
    const key = `${path}|${color || "#265b4f"}|${targetHeight}`;
    if (markerIconCacheRef.current.has(key)) {
      return markerIconCacheRef.current.get(key);
    }
    const img = await loadImageElement(path);
    const aspect = (img.naturalWidth || 1) / (img.naturalHeight || 1);
    const height = targetHeight;
    const width = Math.max(1, Math.round(height * aspect));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    ctx.globalCompositeOperation = "source-in";
    ctx.fillStyle = color || "#265b4f";
    ctx.fillRect(0, 0, width, height);
    const dataUrl = canvas.toDataURL("image/png");
    markerIconCacheRef.current.set(key, dataUrl);
    return dataUrl;
  };

  const ensureMarkerImage = async (mapInstance, layerId, color, type = "default") => {
    const imageId = `marker-icon-${layerId}-${type}`;
    if (markerImagesRef.current.has(imageId)) return imageId;
    const path = CURSOR_ICON_PATHS[type] || CURSOR_ICON_PATHS.default;
    const targetHeight = ICON_TARGET_HEIGHT;
    // Virtual icon has a white background that breaks color tinting — resize without tinting
    let img;
    if (type === "virtual") {
      const raw = await loadImageElement(path);
      const aspect = (raw.naturalWidth || 1) / (raw.naturalHeight || 1);
      const h = targetHeight;
      const w = Math.max(1, Math.round(h * aspect));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(raw, 0, 0, w, h);
      img = await loadImageElement(canvas.toDataURL("image/png"));
    } else {
      img = await loadImageElement(await loadTintedIcon(path, color || "#265b4f", targetHeight));
    }
    if (mapInstance.hasImage(imageId)) {
      mapInstance.removeImage(imageId);
    }
    mapInstance.addImage(imageId, img, { pixelRatio: ICON_PIXEL_RATIO });
    markerImagesRef.current.add(imageId);
    return imageId;
  };

  const normalizeFeatureProps = (
    props = {},
    lang = languageRef.current,
    storeRef
  ) => {
    if (props.id && storeRef && storeRef.current) {
      storeRef.current.set(props.id, props);
    }
    const address = firstAddress(props.addresses);
    const taxonomiesList = toTaxonomyArray(props.taxonomies)
      .slice()
      .sort((a, b) => {
        const layerA = a.layer_id || 0;
        const layerB = b.layer_id || 0;
        if (layerA !== layerB) return layerA - layerB;
        const la = (pickLabel(a, lang) || "").toLowerCase();
        const lb = (pickLabel(b, lang) || "").toLowerCase();
        return la.localeCompare(lb);
      });
    const taxonomyCodes = taxonomiesList.map((t) => t.code).filter(Boolean);
    const taxonomyColor = taxonomyCodes
      .map((code) => taxonomyColorMapRef.current[code])
      .find(Boolean);
    const color =
      props.color ||
      taxonomyColor ||
      layerColorMapRef.current[props.layer_id] ||
      "#265b4f";
    const precision = (address.precision_level || "").toLowerCase();
    const markerType =
      precision === "city"
        ? "approximative"
        : precision === "virtual"
        ? "virtual"
        : "default";
    const markerIconId = `marker-icon-${props.layer_id}-${markerType}`;
    const endYear = props.end_year ?? props.endYear ?? null;
    const entType = props.ent_type || props.entType || props.type || "";
    const relations = toArray(props.relations);
    const aliases = toArray(props.aliases);
    const entTypeLabel =
      props.ent_type &&
      props.ent_type.toLowerCase() === "organization" &&
      t(lang, "organization");

    return {
      id: props.id,
      NOM: pickLabel(props, lang),
      RESPONSABLES:
        props[`label_${lang}`] ||
        entTypeLabel ||
        props.ent_type ||
        t(lang, "organization"),
      ADRESS: [address.address_line1, address.address_line2]
        .filter(Boolean)
        .join(" "),
      CP: address.zip || "",
      VILLE: address.city || "",
      PHONE:
        findContacts(props.contacts || [], "phone")[0] ||
        findContacts(props.contacts || [], "tel")[0] ||
        findContacts(props.contacts || [], "mobile")[0] ||
        "",
      PHONE_MULTI: findContacts(props.contacts || [], "phone"),
      EMAIL:
        findContacts(props.contacts || [], "mail")[0] ||
        findContacts(props.contacts || [], "email")[0] ||
        "",
      EMAIL_MULTI: findContacts(props.contacts || [], "mail").concat(
        findContacts(props.contacts || [], "email")
      ),
      WEB:
        findContacts(props.contacts || [], "web")[0] ||
        findContacts(props.contacts || [], "site")[0] ||
        findContacts(props.contacts || [], "url")[0] ||
        "",
      WEB_MULTI: findContacts(props.contacts || [], "web").concat(
        findContacts(props.contacts || [], "site"),
        findContacts(props.contacts || [], "url")
      ),
      TAGS: formatTagsString(taxonomiesList, lang),
      DESCRIPTION:
        (lang === "en" && props.description_public_en) ||
        (lang === "nl" && props.description_public_nl) ||
        props.description_public ||
        "",
      DATE: props.start_year || "",
      END_YEAR: endYear,
      color,
      taxonomies: taxonomiesList,
      addresses: toArray(props.addresses),
      contacts: toArray(props.contacts),
      layer_id: props.layer_id,
      marker_icon_id: markerIconId,
      ent_type: entType,
      relations,
      aliases,
    };
  };

  useEffect(() => {
    // When filters change, clear caches so next viewport change refetches correctly.
    console.log("[entities] clear caches due to filter change", {
      activeLayerIds,
      activeTaxonomyCodes,
      historyYearFrom,
      historyYearTo,
    });
    entitiesCacheRef.current.clear();
    coverageCacheRef.current.clear();
    lastFetchKeyRef.current = null;
    setHasLoadedOnce(false);
  }, [activeLayerIds, activeTaxonomyCodes, historyYearFrom, historyYearTo]);

  useEffect(() => {
    if (historyActive) return;
    setShouldAutoFitNextFetch(true);
  }, [selectedTags, selectedLayers, effectiveSearchQuery, historyActive]);

  useEffect(() => {
    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    const mapInstance = new mapboxgl.Map({
      container: "map",
      style: MAP_STYLE,
      center: [4.47327, 50.64273],
      zoom: isMobile ? 6.1 : 8.18,
      minZoom: isMobile ? 6.1 : 7.5,
    });

    mapRef.current = mapInstance;
    setMap(mapInstance);

    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: "custom-popup",
    });

    const updateBbox = ({ resizeIfInvalid = false } = {}) => {
      const bounds = mapInstance.getBounds();
      const w = bounds.getWest();
      const s = bounds.getSouth();
      const e = bounds.getEast();
      const n = bounds.getNorth();
      if (!isFinite(w) || !isFinite(s) || !isFinite(e) || !isFinite(n)) {
        if (resizeIfInvalid) {
          // Safari: container not yet sized, force resize and retry once
          try { mapInstance.resize(); } catch (_) { /* ignore */ }
          setTimeout(() => updateBbox({ resizeIfInvalid: false }), 150);
        }
        return;
      }
      setViewportBbox([w, s, e, n]);
    };

    const handleMoveEnd = () => {
      if (moveEndTimeout.current) {
        clearTimeout(moveEndTimeout.current);
      }
      moveEndTimeout.current = setTimeout(updateBbox, 300);
    };

    mapInstance.on("load", () => {
      setMapReady(true);
      try {
        mapInstance.resize();
      } catch (e) {
        console.warn("[map] resize on load failed", e);
      }
      updateBbox({ resizeIfInvalid: true });
      // Hide any legacy layers baked into the style that overlap our data
      STYLE_NOISE_LAYERS.forEach((layerId) => {
        if (mapInstance.getLayer(layerId)) {
          try {
            mapInstance.removeLayer(layerId);
          } catch (e) {
            mapInstance.setLayoutProperty(layerId, "visibility", "none");
          }
        }
        if (mapInstance.getSource(layerId)) {
          try {
            mapInstance.removeSource(layerId);
          } catch (e) {
            // ignore if in use
          }
        }
      });
      // Remove any other symbol layers pointing to known old sources
      const style = mapInstance.getStyle();
      console.log(
        "Map style layers:",
        style.layers.map((l) => ({ id: l.id, source: l.source }))
      );
      const candidateSources = new Set(
        style.layers
          .filter(
            (l) =>
              l.source &&
              STYLE_NOISE_LAYERS.some((id) =>
                l.source.toLowerCase().includes(id.toLowerCase())
              )
          )
          .map((l) => l.source)
      );
      style.layers.forEach((layer) => {
        if (candidateSources.has(layer.source)) {
          if (mapInstance.getLayer(layer.id)) {
            try {
              mapInstance.removeLayer(layer.id);
            } catch (e) {
              mapInstance.setLayoutProperty(layer.id, "visibility", "none");
            }
          }
        }
      });
      candidateSources.forEach((srcId) => {
        if (mapInstance.getSource(srcId)) {
          try {
            mapInstance.removeSource(srcId);
          } catch (e) {
            // ignore if locked
          }
        }
      });
      console.log(
        "Remaining layers after prune:",
        mapInstance
          .getStyle()
          .layers.map((l) => ({ id: l.id, source: l.source }))
      );
    });
    mapInstance.on("moveend", handleMoveEnd);

    let lastHoveredFeature = null;

    mapInstance.on("mousemove", (e) => {
      if (!mapRef.current || isMobile) return;
      const features = mapInstance.queryRenderedFeatures(e.point);
      const hoveredFeature = features.find(
        (f) => f.properties && f.properties.layer_id
      );
      if (hoveredFeature) {
        lastHoveredFeature = hoveredFeature;
        const label = pickLabel(hoveredFeature.properties, languageRef.current);
        if (label) {
          popup.setLngLat(e.lngLat).setHTML(label).addTo(mapInstance);
          const popupElement = popup.getElement();
          if (popupElement) {
            popupElement.addEventListener("click", () => {
              if (lastHoveredFeature) {
                handleFeatureSelection(
                  normalizeFeatureProps(
                    lastHoveredFeature.properties,
                    languageRef.current,
                    featurePropsByIdRef
                  ),
                  { collapseInfoBoxOnMobile: true }
                );
              }
            });
          }
        }
      } else {
        popup.remove();
        lastHoveredFeature = null;
      }
    });

    mapInstance.on("click", (e) => {
      const features = mapInstance.queryRenderedFeatures(e.point);
      const clickedFeature = features.find(
        (f) => f.properties && f.properties.layer_id
      );
      if (clickedFeature) {
        const normalized = normalizeFeatureProps(
          clickedFeature.properties,
          languageRef.current,
          featurePropsByIdRef
        );
        handleFeatureSelection(normalized, { collapseInfoBoxOnMobile: true });
      }
    });

    return () => {
      setMapReady(false);
      mapInstance.remove();
      window.removeEventListener("resize", handleResize);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    layersAbortRef.current?.abort();
    const controller = new AbortController();
    layersAbortRef.current = controller;

    fetchLayers(controller.signal, true)
      .then((payload) => {
        if (controller.signal.aborted) return;
        const data = payload?.data || payload || [];
        const fetchedLayers = Array.isArray(data) ? data : [];
        setLayers(fetchedLayers);
        const filterableTaxonomies = fetchedLayers.flatMap((layer) =>
          (layer.taxonomies || [])
            .filter((t) => t.is_filtrable)
            .map((tax) => ({
              ...tax,
              layer_id: tax.layer_id || layer.id,
              color: tax.color || layer.color,
            }))
        );
        setTaxonomies(filterableTaxonomies);
        setSelectedLayers(fetchedLayers.map((l) => l.id));
        setSelectedTags(
          filterableTaxonomies.map((t) => t.code).filter(Boolean)
        );

        const layerColors = fetchedLayers.reduce((acc, layer) => {
          acc[layer.id] = layer.color;
          return acc;
        }, {});
        setLayerColorMap(layerColors);
        layerColorMapRef.current = layerColors;

        const taxonomyColors = filterableTaxonomies.reduce((acc, tax) => {
          if (tax.code) {
            acc[tax.code] = tax.color || layerColors[tax.layer_id];
          }
          return acc;
        }, {});
        setTaxonomyColorMap(taxonomyColors);
        taxonomyColorMapRef.current = taxonomyColors;
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setFetchError(err.message || "Unable to load layers.");
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!map || !layers.length || !mapReady) return;

    layers.forEach(async (layer) => {
      const sourceId = `entities-${layer.id}`;
      const mapLayerId = `entities-layer-${layer.id}`;
      const visibility = selectedLayers.includes(layer.id) ? "visible" : "none";

      if (!map.getSource(sourceId)) {
        map.addSource(sourceId, {
          type: "geojson",
          data: EMPTY_GEOJSON,
        });
      }

      try {
        await Promise.all(
          ["default", "approximative", "virtual"].map((type) =>
            ensureMarkerImage(map, layer.id, layer.color, type)
          )
        );
        if (map.getLayer(mapLayerId)) {
          map.removeLayer(mapLayerId);
        }
        const beforeId = map
          .getStyle()
          .layers.find((l) => l.type === "symbol" && l.source !== sourceId)?.id;
        map.addLayer(
          {
            id: mapLayerId,
            type: "symbol",
            source: sourceId,
            layout: {
              "icon-image": [
                "coalesce",
                ["get", "marker_icon_id"],
                `marker-icon-${layer.id}-default`,
              ],
              "icon-size": 0.8,
              "icon-allow-overlap": true,
              "icon-ignore-placement": true,
              visibility,
            },
          },
          beforeId
        );
      } catch (e) {
        console.error("Unable to load marker icon", e);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, layers, selectedLayers, mapReady]);

  useEffect(() => {
    if (!map || !layers.length || !viewportBbox || !mapReady) return;
    if (isListMode) return;

    setMapFetchInFlight(true);
    if (retryIfEmptyTimerRef.current) {
      clearTimeout(retryIfEmptyTimerRef.current);
      retryIfEmptyTimerRef.current = null;
    }

    const {
      layerIds: activeLayerIdsForFetch,
      taxonomyCodes: activeTaxonomyCodesForFetch,
      bbox: bboxForFetch,
    } = historyFilterOverrides;
    const allowCoverageReuse = !(
      effectiveSearchQuery && effectiveSearchQuery.trim()
    );

    const url = buildEntitiesUrl({
      layerIds: activeLayerIdsForFetch,
      taxonomyCodes: activeTaxonomyCodesForFetch,
      q: effectiveSearchQuery?.trim() || undefined,
      bbox: bboxForFetch,
      perPage: 0,
      fields: "lite",
      format: "geojson",
      yearFrom: historyYearFrom,
      yearTo: historyYearTo,
    });
    const fetchKey = url;
    const coverageKey = JSON.stringify({
      layers: [...activeLayerIdsForFetch].sort(),
      tags: [...activeTaxonomyCodesForFetch].sort(),
      q: effectiveSearchQuery?.trim() || "",
      yearFrom: historyYearFrom ?? null,
      yearTo: historyYearTo ?? null,
      fields: "lite",
    });

    const bboxContains = (outer, inner) => {
      if (!outer || !inner) return false;
      const [ow, os, oe, on] = outer;
      const [iw, is, ie, inb] = inner;
      return ow <= iw && os <= is && oe >= ie && on >= inb;
    };

    const bboxUnion = (a, b) => [
      Math.min(a[0], b[0]),
      Math.min(a[1], b[1]),
      Math.max(a[2], b[2]),
      Math.max(a[3], b[3]),
    ];

    if (fetchKey === lastFetchKeyRef.current) {
      console.log("[entities] skip fetch (same key)", { fetchKey });
      setMapFetchInFlight(false);
      return;
    }

    const cachedCoverage = coverageCacheRef.current.get(coverageKey);
    if (
      allowCoverageReuse &&
      cachedCoverage &&
      bboxContains(cachedCoverage.bbox, viewportBbox)
    ) {
      console.log("[entities] use coverage cache", {
        coverageKey,
        bboxForFetch,
        cachedBbox: cachedCoverage.bbox,
      });
      layers.forEach((layer) => {
        const sourceId = `entities-${layer.id}`;
        const source = map.getSource(sourceId);
        if (source && source.setData) {
          source.setData({
            type: "FeatureCollection",
            features: cachedCoverage.grouped[layer.id] || [],
          });
        }
      });
      const coverageCount = Object.values(cachedCoverage.grouped || {}).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
      );
      setLastFetchEmpty(coverageCount === 0);
      setCurrentFeature(null);
      lastFetchKeyRef.current = fetchKey;
      setMapFetchInFlight(false);
      return;
    }

    if (entitiesCacheRef.current.has(fetchKey)) {
      const grouped = entitiesCacheRef.current.get(fetchKey);
      layers.forEach((layer) => {
        console.log("[entities] use entities cache", { fetchKey });
        const sourceId = `entities-${layer.id}`;
        const source = map.getSource(sourceId);
        if (source && source.setData) {
          source.setData({
            type: "FeatureCollection",
            features: grouped[layer.id] || [],
          });
        }
      });
      const groupedCount = Object.values(grouped || {}).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0
      );
      setLastFetchEmpty(groupedCount === 0);
      setCurrentFeature(null);
      lastFetchKeyRef.current = fetchKey;
      setMapFetchInFlight(false);
      return;
    }

    entitiesAbortRef.current?.abort();
    const controller = new AbortController();
    entitiesAbortRef.current = controller;
    setIsLoading(true);
    setFetchError(null);
    setLastFetchEmpty(false);
    if (firstFetchTimeoutRef.current) {
      clearTimeout(firstFetchTimeoutRef.current);
    }
    firstFetchTimeoutRef.current = setTimeout(() => {
      if (!controller.signal.aborted) {
        console.warn("[entities] fetch timeout, aborting and retrying once");
        controller.abort();
        entitiesAbortRef.current = null;
        setShouldRetryFirstFetch(true);
      }
    }, 10000);

    console.log("[entities] fetch start", {
      url,
      fetchKey,
      coverageKey,
      activeLayerIdsForFetch,
      activeTaxonomyCodesForFetch,
      bboxForFetch,
    });

    fetchEntities(url, controller.signal)
      .then((payload) => {
        if (firstFetchTimeoutRef.current) {
          clearTimeout(firstFetchTimeoutRef.current);
          firstFetchTimeoutRef.current = null;
        }
        if (controller.signal.aborted) return;
        const fc =
          payload?.type === "FeatureCollection"
            ? payload
            : payload?.data || EMPTY_GEOJSON;
        const features = fc?.features || [];
        setLastFetchEmpty(features.length === 0);
        const total =
          payload?.meta?.total ??
          payload?.total ??
          (payload?.data && payload.data.total);
        if (typeof total === "number") {
          setTotalAvailable(total);
        }
        const grouped = {};

        features.forEach((feature) => {
          const props = feature.properties || {};
          const taxonomyLayerIds = toTaxonomyArray(props.taxonomies)
            .map((t) => t.layer_id)
            .filter(Boolean);
          const primaryLayerId =
            props.layer_id ||
            props.layerId ||
            props.layer ||
            props.layers?.[0] ||
            taxonomyLayerIds[0];
          // In history mode, prefer HISTORY_DEFAULT_LAYER_IDS so entities
          // that span multiple layers still appear on the map
          const historyPreferredId =
            bboxForFetch === undefined
              ? HISTORY_DEFAULT_LAYER_IDS.find((id) =>
                  taxonomyLayerIds.includes(id)
                )
              : undefined;
          const layerId = historyPreferredId || primaryLayerId;
          if (!layerId) return;

          const taxonomyCodes =
            toTaxonomyArray(props.taxonomies)
              .map((t) => t.code)
              .filter(Boolean) || [];
          const taxonomyColor = taxonomyCodes
            .map((code) => taxonomyColorMapRef.current[code])
            .find(Boolean);
          const color =
            props.color ||
            taxonomyColor ||
            layerColorMapRef.current[layerId] ||
            "#265b4f";
          const precisionLevel = (
            firstAddress(props.addresses)?.precision_level || ""
          ).toLowerCase();
          const markerType =
            precisionLevel === "city"
              ? "approximative"
              : precisionLevel === "virtual"
              ? "virtual"
              : "default";
          const markerIconId = `marker-icon-${layerId}-${markerType}`;

          const normalizedFeature = {
            ...feature,
            properties: {
              ...props,
              layer_id: layerId,
              id: props.id || feature.id,
              color,
              marker_icon_id: markerIconId,
            },
          };

          grouped[layerId] = grouped[layerId] || [];
          grouped[layerId].push(normalizedFeature);
        });

        layers.forEach((layer) => {
          const sourceId = `entities-${layer.id}`;
          const source = map.getSource(sourceId);
          if (source && source.setData) {
            source.setData({
              type: "FeatureCollection",
              features: grouped[layer.id] || [],
            });
          }
        });
        entitiesCacheRef.current.set(fetchKey, grouped);
        lastFetchKeyRef.current = fetchKey;
        console.log("[entities] fetch success", {
          fetchKey,
          total,
          featureCount: features.length,
        });
        setHasLoadedOnce(true);
        setShouldRetryFirstFetch(false);
        setMapFetchInFlight(false);
        if (shouldAutoFitNextFetch && features.length > 0 && mapRef.current) {
          const bounds = new mapboxgl.LngLatBounds();
          features.forEach((f) => {
            const coords = f.geometry?.coordinates;
            if (
              Array.isArray(coords) &&
              coords.length === 2 &&
              Number.isFinite(coords[0]) &&
              Number.isFinite(coords[1])
            ) {
              bounds.extend(coords);
            }
          });
          if (!bounds.isEmpty()) {
            try {
              mapRef.current.fitBounds(bounds, {
                padding: 40,
                maxZoom: 11,
              });
              setShouldAutoFitNextFetch(false);
            } catch (e) {
              console.warn("[map] fitBounds failed", e);
            }
          }
        }
        // merge coverage for this key
        if (allowCoverageReuse) {
          const existing = coverageCacheRef.current.get(coverageKey);
          const nextBbox = existing
            ? bboxUnion(existing.bbox, viewportBbox)
            : viewportBbox;
          const merged = {};
          const layerIdsAll = new Set([
            ...Object.keys(grouped),
            ...(existing ? Object.keys(existing.grouped) : []),
          ]);
          layerIdsAll.forEach((lid) => {
            const seen = new Set();
            merged[lid] = [];
            const append = (list) =>
              (list || []).forEach((f) => {
                const fid = f.id || f.properties?.id;
                if (fid && seen.has(fid)) return;
                if (fid) seen.add(fid);
                merged[lid].push(f);
              });
            append(existing?.grouped?.[lid]);
            append(grouped[lid]);
          });
          coverageCacheRef.current.set(coverageKey, {
            bbox: nextBbox,
            grouped: merged,
            total: total ?? totalAvailable ?? null,
          });
        }
        setIsLoading(false);
        setCurrentFeature(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setIsLoading(false);
        setFetchError(err.message || "Unable to load entities.");
        if (firstFetchTimeoutRef.current) {
          clearTimeout(firstFetchTimeoutRef.current);
          firstFetchTimeoutRef.current = null;
        }
        if (!hasLoadedOnce) {
          console.warn("[entities] first fetch failed, scheduling retry");
          lastFetchKeyRef.current = null;
          setShouldRetryFirstFetch(true);
          setShouldAutoFitNextFetch(false);
        }
        setLastFetchEmpty(false);
        setMapFetchInFlight(false);
      })
      .finally(() => {
        if (firstFetchTimeoutRef.current) {
          clearTimeout(firstFetchTimeoutRef.current);
          firstFetchTimeoutRef.current = null;
        }
        if (controller.signal.aborted) {
          setIsLoading(false);
        }
        setMapFetchInFlight(false);
      });

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    layers,
    selectedLayers,
    selectedTags,
    activeLayerIds,
    activeTaxonomyCodes,
    historyYearFrom,
    historyYearTo,
    effectiveSearchQuery,
    viewportBbox,
    taxonomies,
    isListMode,
    historyFilterOverrides,
  ]);

  useEffect(() => {
    if (!currentFeature && fullInfoBox) {
      setFullInfoBox(false);
    }
  }, [currentFeature, fullInfoBox]);

  useEffect(() => {
    if (!mapReady || !layers.length) return undefined;
    if (retryIfEmptyTimerRef.current) {
      clearTimeout(retryIfEmptyTimerRef.current);
    }
    retryIfEmptyTimerRef.current = setTimeout(() => {
      if (!hasLoadedOnce && !isLoading) {
        console.warn("[entities] retrying initial fetch due to empty state");
        lastFetchKeyRef.current = null;
        setShouldRetryFirstFetch(true);
        const mapInstance = mapRef.current;
        if (mapInstance) {
          try {
            mapInstance.resize();
          } catch (e) {
            console.warn("[map] resize during retry failed", e);
          }
          const bounds = mapInstance.getBounds();
          setViewportBbox([
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth(),
          ]);
        }
      }
    }, 2500);

    return () => {
      if (retryIfEmptyTimerRef.current) {
        clearTimeout(retryIfEmptyTimerRef.current);
        retryIfEmptyTimerRef.current = null;
      }
    };
  }, [mapReady, layers.length, hasLoadedOnce, isLoading]);

  useEffect(() => {
    if (map && viewMode === "map") {
      try {
        map.resize();
      } catch (e) {
        // ignore resize failures
      }
    }
  }, [map, viewMode]);

  useEffect(() => {
    if (isListMode) {
      entitiesAbortRef.current?.abort();
      setIsLoading(false);
      setMapFetchInFlight(false);
    }
  }, [isListMode]);

  const {
    items: listItemsRaw,
    hasMore: listHasMore,
    isLoading: isListLoading,
    error: listError,
    loadMore: loadMoreList,
    total: listTotal,
  } = useEntityList({
    active: isListMode,
    layerIds: activeLayerIds,
    taxonomyCodes: activeTaxonomyCodes,
    searchQuery: effectiveSearchQuery,
    perPage: 40,
  });

  const listItems = useMemo(
    () =>
      listItemsRaw
        .map((entity) => {
          if (!entity) return null;
          const layerId =
            entity.layer_id ||
            entity.layerId ||
            entity.layer ||
            entity.layers?.[0] ||
            toTaxonomyArray(entity.taxonomies)?.[0]?.layer_id;
          const taxonomyCodes = toTaxonomyArray(entity.taxonomies)
            .map((t) => t.code)
            .filter(Boolean);
          const taxonomyColor = taxonomyCodes
            .map((code) => taxonomyColorMapRef.current[code])
            .find(Boolean);
          const color =
            entity.color ||
            taxonomyColor ||
            layerColorMapRef.current[layerId] ||
            "#265b4f";
          return normalizeFeatureProps(
            { ...entity, layer_id: layerId, color },
            languageRef.current,
            featurePropsByIdRef
          );
        })
        .filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [listItemsRaw, language, layerColorMap, taxonomyColorMap]
  );

  const toggleDescription = (id) => {
    setExpandedDescriptions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    const id = currentFeature?.id;
    if (!id) {
      detailAbortRef.current?.abort();
      detailActiveIdRef.current = null;
      return;
    }
    if (currentFeature?._detailed) return;
    if (entityDetailsCacheRef.current.has(id)) {
      const cached = entityDetailsCacheRef.current.get(id);
      setCurrentFeature((prev) =>
        normalizeFeatureProps(
          {
            ...cached,
            color: prev?.color || cached.color,
            layer_id: prev?.layer_id || cached.layer_id,
            _detailed: true,
          },
          languageRef.current
        )
      );
      return;
    }
    detailAbortRef.current?.abort();
    const controller = new AbortController();
    detailAbortRef.current = controller;
    detailActiveIdRef.current = id;
    fetchEntityDetail(id, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        if (detailActiveIdRef.current !== id) return;
        entityDetailsCacheRef.current.set(id, data);
        setCurrentFeature((prev) =>
          normalizeFeatureProps(
            {
              ...data,
              color: prev?.color || data.color,
              layer_id: prev?.layer_id || data.layer_id,
              _detailed: true,
            },
            languageRef.current
          )
        );
        detailActiveIdRef.current = null;
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("Failed to load entity detail", err);
      });
    return () => controller.abort();
  }, [currentFeature?.id, currentFeature?._detailed, language]);

  useEffect(() => {
    if (!isListMode) return undefined;
    const sentinel = listSentinelRef.current;
    const root = listContainerRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && listHasMore && !isListLoading) {
            loadMoreList();
          }
        });
      },
      {
        root,
        rootMargin: "200px 0px",
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isListMode, listHasMore, isListLoading, loadMoreList]);

  const handleHistoryToggle = (nextValue) => {
    const nextActive =
      typeof nextValue === "boolean" ? nextValue : !historyActive;
    console.log("[history] toggle", { nextActive, viewMode });

    setSearchQuery("");
    setDebouncedSearchQuery("");

    if (nextActive && viewMode === "list") {
      setViewMode("map");
    }

    if (nextActive && !historyActive) {
      historyPrevFiltersRef.current = {
        layers: selectedLayers,
        tags: selectedTags,
      };
      const allowedLayers = layers
        .map((l) => l.id)
        .filter((id) => HISTORY_DEFAULT_LAYER_IDS.includes(id));
      const nextLayers =
        allowedLayers.length > 0
          ? allowedLayers
          : HISTORY_DEFAULT_LAYER_IDS;
      setSelectedLayers(nextLayers);
      setSelectedTags([]);
    } else if (!nextActive && historyActive) {
      const prev = historyPrevFiltersRef.current;
      if (prev) {
        setSelectedLayers(prev.layers || []);
        setSelectedTags(prev.tags || []);
      }
      historyPrevFiltersRef.current = null;
    }

    setHistoryActive(nextActive);
  };

  const handleHistoryRangeChange = (partial) => {
    if (!historyRangeAvailable) return;
    updateHistoryDraft(partial);
  };

  const handleHistoryRangeCommit = () => {
    if (!historyRangeAvailable) return;
    commitHistoryDraft();
  };

  const toggleTag = (code, mode) => {
    const allLayerIds = layers.map((l) => l.id);
    const { selectedLayers: nextLayers, selectedTags: nextTags } =
      getNextTagFilterState({
        taxonomies,
        allLayerIds,
        selectedLayers,
        selectedTags,
        code,
        mode,
      });

    setCurrentFeature(null);
    setSelectedLayers(nextLayers);
    setSelectedTags(nextTags);
  };

  const toggleLayerVisibility = (layerId) => {
    const allLayerIds = layers.map((l) => l.id);
    const { selectedLayers: nextLayers, selectedTags: nextTags } =
      getNextLayerFilterState({
        taxonomies,
        allLayerIds,
        selectedLayers,
        selectedTags,
        layerId,
      });
    setSelectedLayers(nextLayers);
    setSelectedTags(nextTags);
  };

  const selectAllForLayer = (layerId) => {
    const layerCodes = taxonomies
      .filter((t) => t.layer_id === layerId)
      .map((t) => t.code)
      .filter(Boolean);
    if (layerCodes.length === 0) return;
    setSelectedTags((prev) => Array.from(new Set([...prev, ...layerCodes])));
  };

  const sidebarWidth = Math.min(360, Math.max(300, viewportWidth - 40));
  const isOverlaySidebar = isMobile || isNarrow;
  const shouldShowSidebar = isOverlaySidebar ? sidebarOpen : true;
  const isOrgCurrentFeature =
    (currentFeature?.ent_type || "").toLowerCase() === "organization";

  return (
    <div
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {isOverlaySidebar ? (
        <button
          onClick={() => setSidebarOpen((prev) => !prev)}
          style={{
            position: "absolute",
            top: 17,
            left: 14,
            zIndex: 7,
            border: "1px solid #d1d5db",
            background: "rgba(255,255,255,0.95)",
            color: "#265b4f",
            padding: "8px 10px",
            borderRadius: 10,
            boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          {sidebarOpen ? "✕" : "☰"}
        </button>
      ) : null}

      {shouldShowSidebar && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: isOverlaySidebar ? 0 : "auto",
            bottom: 0,
            padding: "0",
            pointerEvents: isOverlaySidebar ? "auto" : "none",
            zIndex: isOverlaySidebar ? 7 : 5,
            background: isOverlaySidebar
              ? "rgba(255,255,255,0.96)"
              : "transparent",
          }}
        >
          <Sidebar
            layers={layers}
            taxonomies={taxonomies}
            selectedLayers={selectedLayers}
            selectedTags={selectedTags}
            toggleLayerVisibility={toggleLayerVisibility}
            toggleTag={toggleTag}
            selectAllForLayer={selectAllForLayer}
            historyActive={historyActive}
            onHistoryToggle={handleHistoryToggle}
            onAboutOpen={() => setAboutOpen(true)}
            t={(key) => t(language, key)}
            language={language}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            showLanguageSwitch={isOverlaySidebar}
            onLanguageChange={setLanguage}
            styleOverrides={{
              width: isOverlaySidebar ? "100%" : sidebarWidth,
              height: isOverlaySidebar ? "100%" : "auto",
              margin: isOverlaySidebar ? "0" : "16px 0 16px 16px",
              borderRadius: isOverlaySidebar ? 0 : 8,
              boxShadow: isOverlaySidebar
                ? "0 10px 26px rgba(0,0,0,0.18)"
                : undefined,
              pointerEvents: "auto",
              ...(isOverlaySidebar ? { padding: "16px" } : {}),
            }}
          />
          {isOverlaySidebar && sidebarOpen ? (
            <button
              onClick={() => setSidebarOpen(false)}
              style={{
                position: "absolute",
                top: 15,
                right: 12,
                zIndex: 8,
                border: "1px solid #E0DFDE",
                background: "rgba(255,255,255,0.95)",
                color: "#265b4f",
                padding: "8px 10px",
                borderRadius: 10,
                boxShadow: "0 6px 12px rgba(0,0,0,0.12)",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          ) : null}
        </div>
      )}

      {!isListMode && fetchError ? (
        <div
          style={{
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#ffe8e6",
            color: "#9b1c1c",
            padding: "8px 12px",
            borderRadius: 8,
            zIndex: 5,
          }}
        >
          {fetchError}
        </div>
      ) : null}
      {!isListMode && (isLoading || lastFetchEmpty) ? (
        <div
          style={{
            position: "absolute",
            bottom: isMobile ? 14 : 18,
            right: isMobile ? 14 : 18,
            zIndex: 6,
            pointerEvents: "none",
          }}
        >
          {isLoading ? (
            <div
              className="dm-spinner"
              style={{
                width: 18,
                height: 18,
                borderWidth: 2,
              }}
              aria-label={t(language, "loading")}
            />
          ) : lastFetchEmpty ? (
            <div
              style={{
                background: "rgba(255,255,255,0.92)",
                color: "#4b5563",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
                boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
              }}
            >
              {t(language, "noResults")}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* About panel */}
      <AboutPanel open={aboutOpen} onClose={() => setAboutOpen(false)} language={language} />

      {/* Map legend */}
      {!isListMode && (
        <MapLegend hidden={!!currentFeature} t={(key) => t(language, key)} />
      )}

      {/* Search bar overlay (map mode) */}
      {!isListMode && !(isOverlaySidebar && sidebarOpen) && (
        <div
          style={{
            position: "absolute",
            top: 16,
            left: isOverlaySidebar ? 56 : "50%",
            right: isOverlaySidebar ? 12 : "auto",
            transform: isOverlaySidebar ? "none" : "translateX(-50%)",
            zIndex: 6,
            display: "flex",
            alignItems: "center",
            gap: 8,
            width: isOverlaySidebar ? "auto" : "100%",
            pointerEvents: "none",
          }}
        >
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            language={language}
            t={t}
            minLength={SEARCH_MIN_LENGTH}
            width={Math.min(420, viewportWidth - (isOverlaySidebar ? 76 : 24))}
          />
        </div>
      )}

      {/* Language pills overlay */}
      {!isMobile && (
        <div
          style={{
            position: "absolute",
            top: 12,
            right: isMobile ? 30 : 12,
            zIndex: 6,
            display: "flex",
            gap: 6,
          }}
        >
          {["fr", "nl", "en"].map((lng) => (
            <button
              key={lng}
              onClick={() => setLanguage(lng)}
              style={{
                border: "1px solid #e5e7eb",
                background: language === lng ? "#265b4f" : "#E0DFDE",
                color: language === lng ? "white" : "#4b5563",
                borderRadius: 8,
                padding: "6px 10px",
                fontWeight: 600,
                cursor: "pointer",
                fontSize: 12,
                boxShadow:
                  language === lng ? "0 6px 12px rgba(0,0,0,0.08)" : "none",
              }}
            >
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {isListMode ? (
        <div
          ref={listContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: "auto",
            padding: isOverlaySidebar
              ? "80px 14px 28px 14px"
              : `80px 24px 32px ${sidebarWidth + 28}px`,
            background:
              "linear-gradient(180deg, rgba(248,249,251,0.96) 0%, rgba(241,245,249,0.94) 100%)",
            backgroundImage: `url(${listModeBg}), linear-gradient(180deg, rgba(248,249,251,0.96) 0%, rgba(241,245,249,0.94) 100%)`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundAttachment: "fixed",
            zIndex: 1,
          }}
        >
          <div
            style={{
              maxWidth: isMobile ? "100%" : 860,
              margin: "0 auto",
              paddingBottom: 40,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 12,
              }}
            >
              <SearchBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                language={language}
                t={t}
                minLength={SEARCH_MIN_LENGTH}
                width={Math.min(
                  460,
                  viewportWidth - (isOverlaySidebar ? 28 : 120)
                )}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: isMobile ? "flex-start" : "center",
                gap: 6,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 10,
                  justifyContent: isMobile ? "flex-start" : "center",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    color: "#6b7280",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {listTotal ?? listItems.length} {t(language, "results")}
                </div>
              </div>
              {isListLoading ? (
                <div
                  className="dm-spinner"
                  style={{ width: 22, height: 22, borderWidth: 3 }}
                  aria-label={t(language, "loading")}
                />
              ) : null}
            </div>

            {listError ? (
              <div
                style={{
                  background: "#ffe8e6",
                  border: "1px solid #f8b4b4",
                  color: "#9b1c1c",
                  padding: "10px 12px",
                  borderRadius: 10,
                  marginBottom: 12,
                }}
              >
                {listError}
              </div>
            ) : null}

            <div
              style={{
                display: "grid",
                gap: 16,
                padding: isMobile ? "0 4px" : "0 8px",
              }}
            >
              {listItems.map((item) => {
                const layerLabel = pickLabel(
                  layers.find((l) => l.id === item.layer_id) || {},
                  language
                );
                return (
                  <ListCard
                    key={item.id}
                    item={item}
                    layerLabel={layerLabel}
                    isExpanded={expandedDescriptions.has(item.id)}
                    onToggleDescription={() => toggleDescription(item.id)}
                    onSelect={() =>
                      handleFeatureSelection(item, {
                        collapseInfoBoxOnMobile: true,
                      })
                    }
                    translate={(key) => t(language, key)}
                    language={language}
                    isMobile={isMobile}
                  />
                );
              })}

              {!isListLoading && listItems.length === 0 && !listError ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#6b7280",
                    padding: "40px 12px",
                    border: "1px dashed #d1d5db",
                    borderRadius: 12,
                    background: "white",
                  }}
                >
                  {t(language, "results")} : 0
                </div>
              ) : null}
              <div ref={listSentinelRef} style={{ height: 2 }} />
              {isListLoading && listItems.length > 0 ? (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px 0 4px",
                    color: "#6b7280",
                    fontWeight: 600,
                  }}
                >
                  <span
                    className="dm-spinner"
                    aria-label={t(language, "loading")}
                  />
                </div>
              ) : null}
              {!listHasMore && listItems.length > 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#9ca3af",
                    fontSize: 12,
                    paddingBottom: 10,
                  }}
                >
                  • {t(language, "endOfList")} •
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <HistoryRangeBar
        active={historyActive && viewMode === "map"}
        range={historyRange}
        selection={historyDraft}
        loading={isHistoryLoading}
        onChange={handleHistoryRangeChange}
        onCommit={handleHistoryRangeCommit}
        t={(key) => t(language, key)}
      />

      <div
        id="map"
        style={{
          width: "100vw",
          height: "100vh",
          position: "absolute",
          left: 0,
          top: 0,
          zIndex: 0,
          display: isListMode ? "none" : "block",
        }}
      ></div>
      {currentFeature && (fullInfoBox || !isMobile) ? (
        <div
          id="feature-info"
          className="dm-card card-scroll"
          style={{
            position: "absolute",
            bottom: isMobile && historyActive ? 110 : isMobile ? 25 : 15,
            left: isMobile ? 16 : "auto",
            right: isMobile ? 16 : 25,
            maxHeight: "70vh",
            width: isMobile ? "auto" : 400,
            overflowX: "hidden",
            overflowY: "scroll",
            zIndex: 7,
          }}
        >
          <button
            onClick={() => {
              setCurrentFeature(null);
            }}
            style={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#7a7a7a",
              border: "1px solid #e5e5e5",
              background: "#E0DFDE",
              fontSize: "0.9rem",
              cursor: "pointer",
              borderRadius: 6,
              width: 26,
              height: 26,
            }}
          >
            ✕
          </button>
          <h1
            style={{
              fontSize: "1.4rem",
              fontWeight: "700",
              width: "85%",
              lineHeight: "1.2",
              marginBottom: 4,
            }}
          >
            {currentFeature.NOM}
          </h1>
          {currentFeature.RESPONSABLES &&
          currentFeature.RESPONSABLES !== currentFeature.NOM ? (
            <div style={{ color: "#7a7a7a", marginBottom: 10 }}>
              {currentFeature.RESPONSABLES}
            </div>
          ) : null}

          {currentFeature.taxonomies?.length ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 12,
                borderBottom: "1px solid #f0f0f0",
                paddingBottom: 8,
              }}
            >
              {currentFeature.taxonomies.map((tax) => (
                <span
                  key={tax.code || tax.id}
                  className="dm-chip"
                  style={{
                    background: tax.color || "#265b4f",
                  }}
                >
                  {pickLabel(tax, languageRef.current)}
                </span>
              ))}
            </div>
          ) : null}

          {/* <hr style={{ border: 0, borderTop: "1px solid #f0f0f0" }} /> */}

          {currentFeature.DESCRIPTION ? (
            <p
              style={{
                lineHeight: 1.6,
                marginBottom: 12,
                color: "#688C82",
              }}
            >
              {currentFeature.DESCRIPTION}
            </p>
          ) : null}
          {currentFeature.DATE ? (
            <div style={{ marginTop: 4, marginBottom: 6, color: "#688C82" }}>
              <span style={{ fontStyle: "italic" }}>
                {t(language, "creationYear")}:
              </span>{" "}
              {currentFeature.DATE}
            </div>
          ) : null}
          {isOrgCurrentFeature && currentFeature.END_YEAR ? (
            <div style={{ marginBottom: 10, color: "#688C82" }}>
              <span style={{ fontStyle: "italic" }}>
                {t(language, "endYear")}:
              </span>{" "}
              {currentFeature.END_YEAR}
            </div>
          ) : null}
          {currentFeature.aliases?.length ? (
            <div style={{ marginBottom: 12, lineHeight: 1.4, color: "#688C82" }}>
              <div style={{ fontWeight: 400, fontStyle: "italic" }}>
                {t(language, "aliases")}
              </div>
              {currentFeature.aliases.map((alias, idx) => {
                const label = alias.alias_label || alias.label || `alias-${idx}`;
                const typeLabel =
                  alias.alias_type_label ||
                  alias[`label_${language}`] ||
                  alias.label_en ||
                  alias.label_fr ||
                  alias.label_nl ||
                  "";
                return (
                  <div key={`${label}-${idx}`} style={{ marginTop: 4 }}>
                    {label}
                    {typeLabel ? ` - ${typeLabel}` : ""}
                  </div>
                );
              })}
            </div>
          ) : null}
          {currentFeature.relations?.length ? (
            <div style={{ marginBottom: 12, lineHeight: 1.4, color: "#688C82" }}>
              {currentFeature.relations.map((rel, idx) => {
                const relName =
                  rel.label ||
                  rel[`label_${language}`] ||
                  rel.label_en ||
                  rel.label_fr ||
                  rel.label_nl ||
                  rel.id ||
                  `rel-${idx}`;
                const relRole =
                  rel[`label_${language}`] ||
                  rel.label_en ||
                  rel.label_fr ||
                  rel.label_nl ||
                  rel.relation_type ||
                  "";
                return (
                  <div key={`${relName}-${idx}`} style={{ marginTop: 4 }}>
                    {relName}
                    {relRole ? ` - ${relRole}` : ""}
                  </div>
                );
              })}
            </div>
          ) : null}
          {currentFeature.WEB_MULTI?.length ||
          currentFeature.EMAIL_MULTI?.length ||
          currentFeature.PHONE_MULTI?.length ? (
            <div
              style={{
                marginBottom: 12,
                lineHeight: 1.4,
                color: "#688C82",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              {currentFeature.EMAIL_MULTI?.length
                ? Array.from(new Set(currentFeature.EMAIL_MULTI)).map(
                    (mail, idx) => (
                      <a
                        key={`${mail}-${idx}`}
                        href={`mailto:${mail}`}
                        style={{
                          display: "block",
                          color: "#688C82",
                          textDecoration: "none",
                          marginTop: 2,
                        }}
                      >
                        {mail}
                      </a>
                    )
                  )
                : null}

              {currentFeature.PHONE_MULTI?.length
                ? Array.from(new Set(currentFeature.PHONE_MULTI)).map(
                    (phone, idx) => (
                      <div key={`${phone}-${idx}`} style={{ marginTop: 2 }}>
                        {phone}
                      </div>
                    )
                  )
                : null}

              {Array.from(new Set(currentFeature.WEB_MULTI || [])).map(
                (url, idx) => (
                  <a
                    key={`${url}-${idx}`}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      color: "#688C82",
                      textDecoration: "none",
                      fontWeight: 400,
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 16,
                        height: 16,
                        display: "inline-block",
                        backgroundColor: "currentColor",
                        maskImage: "url('/icons/ico_link.svg')",
                        maskRepeat: "no-repeat",
                        maskPosition: "center",
                        maskSize: "contain",
                        WebkitMaskImage: "url('/icons/ico_link.svg')",
                        WebkitMaskRepeat: "no-repeat",
                        WebkitMaskPosition: "center",
                        WebkitMaskSize: "contain",
                      }}
                    />
                    {url}
                  </a>
                )
              )}
            </div>
          ) : null}

          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 8,
              padding: "10px 0",
              borderTop: "1px solid #f0f0f0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontWeight: 600,
                color: "#1f2d3d",
              }}
            >
              {currentFeature.ADRESS ? (
                <>
                  <span style={{ lineHeight: 1.4, color: "#265B4F" }}>
                    {currentFeature.ADRESS}
                    <br />
                    {currentFeature.CP} {currentFeature.VILLE}
                  </span>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : currentFeature ? (
        <>
          <button
            id="feature-info"
            style={{
              position: "absolute",
              bottom: isMobile && historyActive ? 110 : isMobile ? 25 : 15,
              height: 140,
              left: 16,
              right: 16,
              overflowX: "hidden",
              backgroundColor: "#ffffff",
              borderRadius: 16,
              zIndex: 7,
              padding: 12,
              color: "#265b4f",
              overflowY: "scroll",
              textAlign: "left",
              boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
            }}
            onClick={() => setFullInfoBox(true)}
          >
            <button
              type="button"
              style={{
                position: "absolute",
                right: 10,
                bottom: 10,
                fontSize: 12,
                border: "1px solid #e5e7eb",
                background: "#E0DFDE",
                color: "#4b5563",
                fontWeight: 600,
                padding: "4px 8px",
                borderRadius: 8,
                cursor: "pointer",
              }}
            >
              {t(language, "showMore")}
            </button>
            <h1
              style={{
                fontSize: "1.3rem",
                fontWeight: "bold",
                marginLeft: 5,
                textWrap: "nowrap",
                textOverflow: "ellipsis",
                overflow: "hidden",
                width: "90%",
              }}
            >
              {currentFeature.NOM}
            </h1>
            <div
              style={{
                marginLeft: 5,
                marginTop: 10,
                width: "90%",
                flexDirection: "row",
                display: "flex",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              {currentFeature.taxonomies?.map((tax) => (
                <span
                  key={tax.code || tax.id}
                  style={{
                    background: tax.color || "#265b4f",
                    color: "white",
                    borderRadius: 12,
                    padding: "4px 8px",
                    fontWeight: "bold",
                    fontSize: 12,
                  }}
                >
                  {pickLabel(tax, languageRef.current)}
                </span>
              ))}
            </div>
          </button>
        </>
      ) : null}

      {/* <style>{`
        #feature-info::-webkit-scrollbar {
          width: 6px;
        }
        #feature-info::-webkit-scrollbar-thumb {
          background-color: #265b4f;
          border-radius: 10px;
        }
        #feature-info::-webkit-scrollbar-track {
          background: transparent;
          margin-top: 15px;
          margin-bottom: 15px;
        }
      `}</style> */}
    </div>
  );
};

export default MapComponent;
